import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { PlusIcon, SearchIcon } from 'lucide-react';
import { FieldDefinition, LoanRecord } from '../../types/highlights';
const POPOVER_WIDTH = 320;
const POPOVER_MAX_HEIGHT = 340;
const VIEWPORT_MARGIN = 8;
type AddFieldVariant = 'chip' | 'button';
interface AddFieldControlProps {
  availableFields: FieldDefinition[];
  loan: LoanRecord;
  variant: AddFieldVariant;
  heightClass?: string;
  onAdd: (fieldId: string) => void;
}
export function AddFieldControl({
  availableFields,
  loan,
  variant,
  heightClass = 'h-[30px]',
  onAdd
}: AddFieldControlProps) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [position, setPosition] = useState<{
    top: number;
    left: number;
  } | null>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const popoverRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const updatePosition = useCallback(() => {
    const trigger = triggerRef.current;
    if (!trigger) return;
    const rect = trigger.getBoundingClientRect();
    const spaceBelow = window.innerHeight - rect.bottom;
    const openUpward = spaceBelow < POPOVER_MAX_HEIGHT + VIEWPORT_MARGIN && rect.top > spaceBelow;
    const preferredLeft = variant === 'chip' ? rect.left : rect.right - POPOVER_WIDTH;
    const maxLeft = window.innerWidth - POPOVER_WIDTH - VIEWPORT_MARGIN;
    setPosition({
      top: openUpward ? rect.top - POPOVER_MAX_HEIGHT - 8 : rect.bottom + 8,
      left: Math.max(VIEWPORT_MARGIN, Math.min(preferredLeft, maxLeft))
    });
  }, [variant]);
  useLayoutEffect(() => {
    if (!open) return;
    updatePosition();
  }, [open, updatePosition]);
  useEffect(() => {
    if (!open) {
      setQuery('');
      setPosition(null);
      return;
    }
    inputRef.current?.focus();
    const onPointerDown = (event: MouseEvent) => {
      const target = event.target as Node;
      if (!triggerRef.current?.contains(target) && !popoverRef.current?.contains(target)) {
        setOpen(false);
      }
    };
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setOpen(false);
        triggerRef.current?.focus();
      }
    };
    const onReposition = () => updatePosition();
    document.addEventListener('mousedown', onPointerDown);
    document.addEventListener('keydown', onKeyDown);
    window.addEventListener('resize', onReposition);
    window.addEventListener('scroll', onReposition, true);
    return () => {
      document.removeEventListener('mousedown', onPointerDown);
      document.removeEventListener('keydown', onKeyDown);
      window.removeEventListener('resize', onReposition);
      window.removeEventListener('scroll', onReposition, true);
    };
  }, [open, updatePosition]);
  const grouped = useMemo(() => {
    const term = query.trim().toLowerCase();
    const matches = availableFields.filter(field => !term || field.label.toLowerCase().includes(term) || field.category.toLowerCase().includes(term));
    const groups = new Map<string, FieldDefinition[]>();
    matches.forEach(field => {
      const existing = groups.get(field.category);
      if (existing) existing.push(field);else groups.set(field.category, [field]);
    });
    return Array.from(groups.entries());
  }, [availableFields, query]);
  const disabled = availableFields.length === 0;
  const triggerClass = variant === 'chip' ? [`flex ${heightClass} w-full items-center justify-center gap-1.5 whitespace-nowrap rounded border border-dashed px-3 text-[12.5px] font-semibold transition`, disabled ? 'cursor-not-allowed border-slate-200 text-slate-300' : 'border-[#b9cbe8] bg-slate-50 text-[#0b5cab] hover:border-[#0b5cab] hover:bg-white focus:outline-none focus-visible:ring-2 focus-visible:ring-[#0b5cab]'].join(' ') : ['inline-flex items-center gap-1.5 rounded-md border px-2.5 py-1.5 text-[12px] font-semibold transition', disabled ? 'cursor-not-allowed border-slate-200 text-slate-300' : 'border-[#cfe0f5] bg-white text-[#0b5cab] hover:bg-[#f3f8ff] focus:outline-none focus-visible:ring-2 focus-visible:ring-[#0b5cab]'].join(' ');
  return <>
      <button ref={triggerRef} type="button" disabled={disabled} aria-haspopup="dialog" aria-expanded={open} onClick={() => setOpen(value => !value)} className={triggerClass} data-id="element-2" data-mp="2vkp7tugcm38y">
        <PlusIcon className="h-3.5 w-3.5" aria-hidden="true" data-id="element-3" data-mp="34kcb6lsn1j4n" />
        {disabled ? 'All fields added' : 'Add Field'}
      </button>

      {open && position ? createPortal(<div ref={popoverRef} role="dialog" aria-label="Add a field to highlights" style={{
      top: position.top,
      left: position.left,
      width: POPOVER_WIDTH,
      maxHeight: POPOVER_MAX_HEIGHT
    }} className="fixed z-[9999] flex flex-col overflow-hidden rounded-md border border-slate-200 bg-white shadow-xl" data-id="element-4" data-mp="1a6um2ucl4e7i">
          <div className="flex items-center gap-2 border-b border-slate-100 px-3 py-2" data-id="element-5" data-mp="3829nxpjurwc0">
            <SearchIcon className="h-4 w-4 shrink-0 text-slate-400" aria-hidden="true" data-id="element-6" data-mp="3c44tx7cqaxw4" />
            <input ref={inputRef} type="search" value={query} onChange={event => setQuery(event.target.value)} placeholder="Search loan fields" aria-label="Search loan fields" className="w-full border-0 p-0 text-sm text-slate-800 placeholder:text-slate-400 focus:outline-none focus:ring-0" data-id="element-7" data-mp="2y4lsexefakss" />
          </div>

          <div className="min-h-0 flex-1 overflow-y-auto py-1" data-id="element-8" data-mp="ne55l7p0y3jt">
            {grouped.length === 0 ? <p className="px-3 py-6 text-center text-sm text-slate-500" data-id="element-9" data-mp="rde38nn6qlkn">
                No fields match “{query}”.
              </p> : grouped.map(([category, fields]) => <div key={category} className="px-1 py-1" data-id="element-10" data-mp="1vazmgnfgurrw">
                  <p className="px-2 py-1 text-[11px] font-semibold uppercase tracking-wide text-slate-400" data-id="element-11" data-mp="1rafppkyt1q6a">
                    {category}
                  </p>
                  {fields.map(field => <button key={field.id} type="button" onClick={() => {
            onAdd(field.id);
            setOpen(false);
          }} className="flex w-full items-center justify-between gap-3 rounded px-2 py-1.5 text-left hover:bg-[#eef3fb] focus:bg-[#eef3fb] focus:outline-none" data-id="element-12" data-mp="2j2dd8oxyyfar">
                      <span className="truncate text-[13px] text-slate-800" data-id="element-13" data-mp="32rnvoap3neck">{field.label}</span>
                      <span className="shrink-0 truncate text-[12px] text-slate-500" data-id="element-14" data-mp="2xxtepf0vpdh6">
                        {field.getValue(loan)}
                      </span>
                    </button>)}
                </div>)}
          </div>
        </div>, document.body) : null}
    </>;
}